import "./scheduleAssistant.css";
import React, { useState, useEffect } from "react";
import { Modal, Tabs, Table, Radio, Input, Select, Tag, Avatar, Tooltip, Spin, Checkbox, Form, message } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faClose, faSearch, faLocationDot, faCircleCheck, faCircleXmark } from "@fortawesome/free-solid-svg-icons";
import { useLazyQuery, useQuery, useMutation } from "@apollo/client";
import { MapContainer, TileLayer, Marker, Popup, Circle } from "react-leaflet";
import L from "leaflet";
import ReactDOMServer from "react-dom/server";
import "leaflet/dist/leaflet.css";
import dayjs from "dayjs";
import { holidayChecks } from "@src/util/holidayChecks";
import isBetween from "dayjs/plugin/isBetween";
import { UserOutlined } from "@ant-design/icons";
import { getEmployeesForScheduleAssistantQuery } from "@src/util/query/employee.query";
import { scheduleCheckFailureList } from "@src/util/scheduleCheckFailureList";
import { skillQuery } from "@src/util/query/skill.query";
import { resolvePostcodeQuery } from "@src/util/query/postcode.query";
import { findPayDate, findPayCurrency } from '@src/util/findPayDate/findPayDate';
import { currencySymbol } from '@src/util/currency/currency';
import { useDefaultCurrency } from '@src/hooks/useDefaultCurrency';
import Spinner from "@src/components/spinner";
import { getPropertyWithNameQuery } from '@src/util/query/properties.query';
import { publishOpenDutiesMutation } from "@src/util/mutation/schedule.mutation";



dayjs.extend(isBetween);

const haversine = (lat1, lon1, lat2, lon2) => {
    const R = 3958.8;
    const dLat = ((lat2 - lat1) * Math.PI) / 180;
    const dLon = ((lon2 - lon1) * Math.PI) / 180;
    const a =
        Math.sin(dLat / 2) ** 2 +
        Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) * Math.sin(dLon / 2) ** 2;
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

const makePin = (color) =>
    L.divIcon({
        html: ReactDOMServer.renderToString(
            <svg xmlns="http://www.w3.org/2000/svg" width="28" height="38" viewBox="0 0 28 38">
                <path d="M14 0C6.268 0 0 6.268 0 14c0 9.94 14 24 14 24S28 23.94 28 14C28 6.268 21.732 0 14 0z" fill={color} />
                <circle cx="14" cy="14" r="6" fill="white" />
            </svg>
        ),
        className: "",
        iconSize: [28, 38],
        iconAnchor: [14, 38],
        popupAnchor: [0, -38],
    });

// Standard Pay column of a pay table
const STANDARD_PAY = "65ee4f5dee2b8708243143eb";

const greenPin = makePin("#22c55e");
const redPin  = makePin("#ef4444");
const bluePin = makePin("#0091ae");

const runChecks = (emp, site, allowedSkills, optionalSkills, shiftDate, startDate, endDate) => {
    const f = {};

    // Holiday - approved blocks; a request waiting for approval is shown as a note instead
    if (holidayChecks(emp, shiftDate).empHolidayCheck) f.empHolidayCheck = true;

    // Blocked site
    if (emp?.blockedSites?.find((bs) => bs?.siteId == site?._id)) f.blockedSite = true;

    // Branch
    if (!emp?.branch?.find((b) => b?._id == site?.branchDetail?._id)) f.branchCheck = true;

    // Training
    if (!emp?.trainedEmployeeOnSite?.find((te) => te?.siteId == site?._id)) f.trainingCheck = true;

    // Skill
    const missingRequired =
        allowedSkills?.length > 0 &&
        !allowedSkills.every((id) => emp?.employeeskills?.find((s) => s?.skill == id && !s?.isArchive));
    const missingOptional =
        optionalSkills?.length > 0 &&
        !optionalSkills.some((id) => emp?.employeeskills?.find((s) => s?.skill == id && !s?.isArchive));
    if (missingRequired || missingOptional) f.skillCheck = true;

    // Visa expiry
    if (emp?.metadata?.requirevisa === "Yes") {
        const vd = emp?.metadata?.visaexpirydate;
        if (!vd || !dayjs(vd, "YYYY/MM/DD").isAfter(dayjs())) f.visaExpiryCheck = true;
    }

    // Skill expiry
    const expiries = emp?.employeeskills
        ?.filter((s) => [...(allowedSkills||[]), ...(optionalSkills||[])].includes(s?.skill) && !s?.isArchive)
        ?.flatMap((s) => s?.fields?.filter((f) => f?.name === "expirydate")?.map((f) => f?.value) || []);
    if (expiries?.length && !expiries.every((d) => dayjs(d).isAfter(dayjs()))) f.skillExpiryCheck = true;

    // Already working
    if (startDate && endDate && emp?.scheduleCheck?.response?.length > 0) {
        const sc = emp.scheduleCheck.response[0];
        const csdt = dayjs(sc?.firstDate);
        const cedt = dayjs(sc?.shiftEndDateTime);
        if (csdt.isBetween(startDate, endDate, null, "[]") && cedt.isBetween(startDate, endDate, null, "[]")) {
            f.alreadyWorking = true;
        }
    }

    return f;
};

export const ScheduleAssistantModal = ({
    visible, close,
    site, area, title,
    shiftStartTime, shiftEndTime, shiftTime,
    allowedSkilledForSpecificArea,
    optionalSkilledForSpecificArea,
    onSelectEmployee,
    jobId, job,
    groupedDuties,
    // the pay level (pay duty type) of the duty or duties being filled
    payLevelIds = [],
}) => {
    // pay rates saved before currencies were recorded are in the tenant's default currency
    const defaultCurrency = useDefaultCurrency();
    const isBulkMode = !!groupedDuties;
    const [postcode, setPostcode]           = useState(site?.postcode || "");
    const [searchCoords, setSearchCoords]   = useState(null);
    const [geocodeStatus, setGeocodeStatus] = useState("idle"); // idle | loading | ok | notfound
    const [geocodeSource, setGeocodeSource] = useState("");
    const [radius, setRadius]               = useState(10);
    const [radiusUnit, setRadiusUnit]       = useState("mile");
    // "site" = experience at this site only | "other" = experience at other sites | null = all
    const [expFilter, setExpFilter]         = useState(null);
    const [activeTab, setActiveTab]         = useState("table");
    const [employees, setEmployees]         = useState([]);




    // On open: if site has a postcode, auto-geocode it so map/radius are ready
    useEffect(() => {
        if (site?.postcode && !searchCoords) {
            const pc = site.postcode;
            setPostcode(pc);
            setGeocodeStatus("loading");
            runResolvePostcode({ variables: { postcode: pc } }).then((res) => {
                const result = res?.data?.resolvePostcode?.result;
                if (result?.lat && result?.lng) {
                    setSearchCoords({ lat: result.lat, lng: result.lng });
                    setGeocodeStatus("ok");
                    setGeocodeSource(result.source || "");
                } else {
                    setGeocodeStatus("idle");
                }
            });
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const { data: skillData } = useQuery(skillQuery, { fetchPolicy: "cache-first" });

    const [runResolvePostcode, { loading: postcodeLoading }] = useLazyQuery(resolvePostcodeQuery, {
        fetchPolicy: "network-only",
    });
    const skillMap = React.useMemo(() => {
        const m = {};
        skillData?.getSkills?.forEach((s) => { m[s._id] = s.skill; });
        return m;
    }, [skillData]);

    const siteCoords = (() => {
        const loc = site?.location;
        if (loc?.northeast?.lat) return { lat: loc.northeast.lat, lng: loc.northeast.lng };
        if (loc?.lat)            return { lat: loc.lat,           lng: loc.lng };
        return null;
    })();

    const refCoords = searchCoords || siteCoords;

    const startDate = shiftTime?.firstDate && shiftStartTime
        ? dayjs(shiftTime.firstDate + " " + shiftStartTime, "DD/MM/YYYY HH:mm") : null;
    const endDate = shiftTime
        ? shiftTime.secondDate
            ? dayjs(shiftTime.secondDate + " " + shiftEndTime, "DD/MM/YYYY HH:mm")
            : dayjs(shiftTime.firstDate  + " " + shiftEndTime, "DD/MM/YYYY HH:mm")
        : null;

    const [fetchEmployees, { data, loading }] = useLazyQuery(getEmployeesForScheduleAssistantQuery, {
        fetchPolicy: "network-only",
    });

    useEffect(() => {
        if (data?.getEmployeesForScheduleAssistant?.response) {
            setEmployees(data.getEmployeesForScheduleAssistant.response);
        }
    }, [data]);

    // Reset geocode state when postcode text changes
    useEffect(() => {
        setSearchCoords(null);
        setGeocodeStatus("idle");
        setGeocodeSource("");
    }, [postcode]);

    // Resolve postcode via GraphQL (DB → freemaptools → Google)
    const doGeocode = async () => {
        if (!postcode.trim()) return null;
        setGeocodeStatus("loading");
        const res = await runResolvePostcode({ variables: { postcode: postcode.trim() } });
        const result = res?.data?.resolvePostcode?.result;
        if (result?.lat && result?.lng) {
            const coords = { lat: result.lat, lng: result.lng };
            setSearchCoords(coords);
            setGeocodeStatus("ok");
            setGeocodeSource(result.source || "");
            return coords;
        }
        setGeocodeStatus("notfound");
        return null;
    };

    const [availableEmployee, setAvailableEmployee]=useState(false);

    const handleSearch = async () => {
        // Auto-geocode first if postcode entered but not yet resolved
        let coords = refCoords;
        if (postcode.trim() && !searchCoords) {
            coords = await doGeocode();
        }

        fetchEmployees({
            variables: {
                input: {
                    siteId: site?._id,
                    schedule: startDate && endDate ? { startDate, endDate } : {},
                    lat:        coords?.lat  ?? null,
                    lng:        coords?.lng  ?? null,
                    radius:     coords ? radius : null,
                    radiusUnit,
                    experienceAtSite:    expFilter === "site",
                    experienceOtherSite: expFilter === "other",
                    teamMember: team,
                    availableEmployee: availableEmployee,
                },
            },
        });
    };

    // one site rate per person only when every duty being filled has the same pay level
    const distinctPayLevels = [...new Set(payLevelIds.filter(Boolean).map(String))];
    const payLevelId = distinctPayLevels.length === 1 ? distinctPayLevels[0] : null;

    const processed = employees.map((emp) => {
        const failures = runChecks(
            emp, site,
            allowedSkilledForSpecificArea  || [],
            optionalSkilledForSpecificArea || [],
            title, startDate, endDate
        );
        const isSuitable = Object.keys(failures).length === 0;
        const pendingHoliday = holidayChecks(emp, title).empPendingHoliday;

        const empCoords = emp?.location?.coordinates?.length === 2
            ? { lat: emp.location.coordinates[1], lng: emp.location.coordinates[0] }
            : null;

        const distance = refCoords && empCoords
            ? haversine(refCoords.lat, refCoords.lng, empCoords.lat, empCoords.lng)
            : null;

        const nextShift = emp?.scheduleCheck?.response?.[0];

        const restGap = nextShift && startDate
            ? startDate.diff(dayjs(nextShift.shiftEndDateTime), "hour")
            : null;

        const skillNames = emp?.employeeskills
            ?.filter((s) => !s?.isArchive)
            ?.map((s) => skillMap[s?.skill] || null)
            ?.filter(Boolean);

        const failLabels = Object.keys(failures)
            .filter((k) => failures[k])
            .map((k) => scheduleCheckFailureList[k]?.description || k);

        // phone — try multiple common field names
        const phone = emp?.metadata?.contactno
            || emp?.metadata?.phone
            || emp?.metadata?.mobilenumber
            || emp?.metadata?.mobile
            || "—";

        // rate — what pay & bill would use: their own rate on this site (Site Credential, while
        // trained here) for the duty's pay level, otherwise their profile rate
        const siteRateEntries = payLevelId && emp?.trainedEmployeeOnSite?.some((te) => te?.siteId == site?._id)
            ? emp?.sitePayTable?.find((t) => String(t?.payLevelId) === payLevelId)?.payTableMeta?.[STANDARD_PAY]
            : null;
        const isSiteRate = !!findPayDate(siteRateEntries);
        const rateEntries = isSiteRate ? siteRateEntries
            : emp?.metadata?.payrate
            || emp?.metadata?.payrule
            || emp?.metadata?.hourlyrate;
        const rate = findPayDate(rateEntries) || null;

        return {
            ...emp,
            _isSuitable:  isSuitable,
            _failures:    failures,
            _distance:    distance,
            _nextShift:   nextShift,
            _restGap:     restGap,
            _skillNames:  skillNames || [],
            _failLabels:  failLabels,
            _pendingHoliday: pendingHoliday,
            _empCoords:   empCoords,
            _phone:       phone,
            _rate:        rate,
            _isSiteRate:  isSiteRate,
            // the rate's own currency (rates saved without one are in the default)
            _rateSymbol:  currencySymbol(findPayCurrency(rateEntries, defaultCurrency)),
        };
    }).sort((a, b) => {
        // suitable first, then by distance
        if (a._isSuitable !== b._isSuitable) return a._isSuitable ? -1 : 1;
        return (a._distance ?? 9999) - (b._distance ?? 9999);
    });

    const suitable   = processed.filter((e) => e._isSuitable);
    const unsuitable = processed.filter((e) => !e._isSuitable);

    const [selectedRowKeys, setSelectedRowKeys] = useState([]);

    const rowSelection = {
        selectedRowKeys,
        onChange: (keys) => setSelectedRowKeys(keys),
    };

    const [publishOpenDuties, {loading: publishOpenDutiesLoading}] = useMutation(publishOpenDutiesMutation);

    const handlePublish = async()=>{
        try{

            if (!selectedRowKeys.length) {
                message.warning("Please select at least one employee");
                return;
            }

            let duties;

            if (isBulkMode) {
                duties = groupedDuties;
            } else {
                if (!jobId || !job?.firstDate || !job?.shiftEndDateTime) {
                    message.warning("Missing duty information");
                    return;
                }
                const dutyKey = `${job.firstDate}To${job.shiftEndDateTime}`;
                duties = {
                    [dutyKey]: [{
                        _id: jobId,
                        siteId: site?._id,
                        siteAreaId: area?._id,
                        startDateTime: job.firstDate,
                        endDateTime: job.shiftEndDateTime,
                    }]
                };
            }

            await publishOpenDuties({
                variables:{
                    filter:{
                        duties,
                        employeeIds: selectedRowKeys,
                    }
                }
            });

            const dutyCount = isBulkMode ? Object.keys(groupedDuties).length : 1;
            message.success(`${dutyCount} ${dutyCount===1?'duty':'duties'} published to ${selectedRowKeys.length} employee${selectedRowKeys.length > 1 ? 's' : ''}`);
            close();

        }catch(err){
            console.log(err, "err");
            message.error(err.message || "Please try again");
        }
    }

    const columns = [
        
        {
            title: "Name",
            key: "name",
                fixed: "left",
            minWidth: 200,
            render: (_, r) => (
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <Avatar
                        size={34}
                        src={r?.metadata?.imageURL}
                        icon={!r?.metadata?.imageURL && <UserOutlined />}
                        style={{ background: "#d9d9d9", flexShrink: 0 }}
                    />
                    <div>
                        <div style={{ fontWeight: 600, fontSize: 12, lineHeight: 1.3 }}>
                            {r.firstname} {r.lastname || ""}
                        </div>
                        <div style={{ color: "#888", fontSize: 10 }}>{r?.metadata?.employmenttype || ""}</div>
                    </div>
                </div>
            ),
        },
        {
            title: "Phone",
            key: "phone",
            minWidth: 200,
            render: (_, r) => <span style={{ fontSize: 12 }}>{r._phone}</span>,
        },
        {
            title: "Rest Period",
            key: "rest",
            minWidth: 200,
            render: (_, r) => {
                if (r._restGap === null) return <span style={{ color: "#bbb", fontSize: 12 }}>—</span>;
                const ok = r._restGap >= 8;
                return (
                    <Tag color={ok ? "green" : "red"} style={{ fontSize: 11 }}>
                        {r._restGap}h gap
                    </Tag>
                );
            },
        },
        {
            title: "Next Shift",
            key: "nextShift",
            minWidth: 200,
            render: (_, r) => r._nextShift
                ? <span style={{ fontSize: 12 }}>{dayjs(r._nextShift.firstDate).format("DD/MM HH:mm")}</span>
                : <span style={{ color: "#bbb", fontSize: 12 }}>—</span>,
        },
        {
            title: "Skills",
            key: "skills",
            minWidth: 200,
            render: (_, r) => r._skillNames.length === 0
                ? <span style={{ color: "#bbb", fontSize: 12 }}>—</span>
                : (
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
                        {r._skillNames.slice(0, 2).map((s, i) => (
                            <Tag key={i} style={{ fontSize: 12, margin: 0,  overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{s}</Tag>
                        ))}
                        {r._skillNames.length > 2 && (
                            <Tooltip title={r._skillNames.slice(2).join(", ")}>
                                <Tag style={{ fontSize: 12, margin: 0, cursor: "pointer" }}>+{r._skillNames.length - 2}</Tag>
                            </Tooltip>
                        )}
                    </div>
                ),
        },
        {
            title: "Checks Failed",
            key: "checks",
            minWidth: 200,
            render: (_, r) => (
                <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                    {r._failLabels.length === 0
                        ? <Tag color="green" style={{ fontSize: 10, margin: 0, width: "fit-content" }}>All passed</Tag>
                        : r._failLabels.map((l, i) => (
                            <Tag key={i} color="red" style={{ fontSize: 12, margin: 0, whiteSpace: "normal", lineHeight: 1.3 }}>
                                {l}
                            </Tag>
                        ))}
                    {r._pendingHoliday && (
                        <Tag color="gold" style={{ fontSize: 12, margin: 0, whiteSpace: "normal", lineHeight: 1.3 }}>
                            Leave Request Pending Approval
                        </Tag>
                    )}
                </div>
            ),
        },
        {
            title: "Distance",
            key: "distance",
            minWidth: 200,
            sorter: (a, b) => (a._distance ?? 9999) - (b._distance ?? 9999),
            render: (_, r) => r._distance != null
                ? <span style={{ fontSize: 12 }}>{r._distance.toFixed(1)} mi</span>
                : <span style={{ color: "#bbb", fontSize: 12 }}>—</span>,
        },
        {
            title: "Rate",
            key: "rate",
            minWidth: 200,
            render: (_, r) => r._rate
                ? r._isSiteRate
                    ? <Tooltip title="Their own rate on this site (Site Credential)">
                        <span style={{ fontSize: 12 }}>{r._rateSymbol}{r._rate} <Tag style={{ fontSize: 11, marginLeft: 4 }}>Site rate</Tag></span>
                      </Tooltip>
                    : <span style={{ fontSize: 12 }}>{r._rateSymbol}{r._rate}</span>
                : <span style={{ color: "#bbb", fontSize: 12 }}>—</span>,
        },
        {
            title: "Weekly Hrs",
            key: "weeklyHrs",
            minWidth: 200,
            render: () => <span style={{ color: "#bbb", fontSize: 12 }}>—</span>,
        },
        // {
        //     title: "",
        //     key: "action",
        //     fixed: "right",
        //     minWidth: 200,
        //     render: (_, r) => (
        //         <button
        //             className={r._isSuitable ? "drawer-filled-btn" : "drawer-outlined-btn"}
        //             // style={{ fontSize: 11, padding: "3px 10px", height: "auto" }}
        //             onClick={() => { handleAssign(r); }}
        //         >
        //             Assign
        //         </button>
        //     ),
        // },
    ];

    const mapCenter = refCoords ? [refCoords.lat, refCoords.lng] : [51.505, -0.09];
    const [team, setTeam] = useState([]);

    const {data: propWithNameData, loading: propWithNameLoading} = useQuery(getPropertyWithNameQuery,{
        variables:{
            name: "memberofteam"
        },
        fetchPolicy: 'network-only'
    });

    return (
        <Modal open={visible} width="100%" style={{ top: 8 }} footer={null} closable={false} destroyOnClose>
            {/* Header */}
            <div
                className="modal-header-title"
            >
                <span>
                    Schedule Assistant &nbsp;
                </span>
                <span onClick={close}><FontAwesomeIcon className="close" icon={faClose} /></span>
            </div>

            <div style={{ display: "flex", height: "76vh" }}>
                {/* ── Sidebar ── */}
                <div
                    style={{
                        width: 210,
                        borderRight: "1px solid #f0f0f0",
                        padding: "14px 12px",
                        display: "flex",
                        flexDirection: "column",
                        gap: 14,
                        overflowY: "auto",
                        background: "#fafafa",
                    }}
                >
                    <div style={{ fontWeight: 600, fontSize: 13, color: "#0091ae" }}>Search available</div>

                    {/* Postcode */}
                    <div>
                        <label style={{ fontSize: 12, display: "block", marginBottom: 4, color: "#555" }}>
                            Search by Postcode
                        </label>
                        <Input
                            className="generic-input-control"
                            placeholder="e.g. SW1A 1AA"
                            value={postcode}
                            onChange={(e) => setPostcode(e.target.value)}
                            onPressEnter={doGeocode}
                            suffix={
                                (geocodeStatus === "loading" || postcodeLoading)
                                    ? <Spin size="small" />
                                    : (
                                        <FontAwesomeIcon
                                            icon={faLocationDot}
                                            title="Locate postcode"
                                            style={{
                                                color: geocodeStatus === "ok"       ? "#22c55e"
                                                     : geocodeStatus === "notfound" ? "#ef4444"
                                                     : "#0091ae",
                                                cursor: "pointer",
                                            }}
                                            onClick={doGeocode}
                                        />
                                    )
                            }
                        />
                        {geocodeStatus === "ok" && searchCoords && (
                            <div style={{ fontSize: 10, color: "#22c55e", marginTop: 3 }}>
                                ✓ Located via {geocodeSource} · {searchCoords.lat.toFixed(4)}, {searchCoords.lng.toFixed(4)}
                            </div>
                        )}
                        {geocodeStatus === "notfound" && (
                            <div style={{ fontSize: 10, color: "#ef4444", marginTop: 3 }}>
                                ✗ Postcode not found
                            </div>
                        )}
                    </div>

                    {/* Experience */}
                    <div className="teamfield">
                        {

                            isBulkMode? null 
                            :
                            <Form.Item>

                                <label style={{ fontSize: 12, display: "block", marginBottom: 6, color: "#555" }}>Employees</label>
                                <Checkbox checked={availableEmployee} onChange={(e)=>setAvailableEmployee(e.target.checked)}   style={{ fontSize: 12 }}> Available employees </Checkbox>

                            </Form.Item>
                        }
                   
                        <Form.Item>


                            <label style={{ fontSize: 12, display: "block", marginBottom: 6, color: "#555" }}>Team</label>


                            {team.length>0 ?
                            team?.map((t)=>(
                                <Tag closable  
                                style={{marginBottom:'5px'}}
                                onClose={()=>setTeam(team?.filter(tm=>tm!=t))}
                                >{t}</Tag>
                            )): null}


                            <Select
                                showSearch
                                optionFilterProp="children"
                                // style={{ width: '55%' }}
                                className="custom-select"
                                suffixIcon={loading? <Spinner/> :<div className="dropdowncaret"></div>}
                                placeholder="Select team"
                                onChange={(e)=>{e?.length>0?setTeam([...team, e]):null}}
                                allowClear
                                // value={}
                                loading={propWithNameLoading}
                                disabled={propWithNameLoading}
                            >
                                <Option value={null} hidden disabled> Select Team </Option>
                                {
                                    propWithNameData?.getPropertywithName?.data?.options?.map((op)=>(
                                        <Option value={op?.key} disabled={team?.find((t)=>t==op?.key)}> {op?.value} </Option>
                                    ))
                                }

                            </Select>

                        </Form.Item>

                       

                       
                    </div>

                    <div>
                        <label style={{ fontSize: 12, display: "block", marginBottom: 6, color: "#555" }}>Experience</label>
                        <Radio.Group
                            value={expFilter}
                            onChange={(e) => setExpFilter(e.target.value === expFilter ? null : e.target.value)}
                            style={{ display: "flex", flexDirection: "column", gap: 6 }}
                        >
                            <Radio value="site"  style={{ fontSize: 12 }}>Experience at the Site</Radio>
                            <Radio value="other" style={{ fontSize: 12 }}>Experience other than this site</Radio>
                        </Radio.Group>
                        {expFilter && (
                            <span
                                style={{ fontSize: 11, color: "#0091ae", cursor: "pointer", marginTop: 4, display: "block" }}
                                onClick={() => setExpFilter(null)}
                            >
                                Clear
                            </span>
                        )}
                    </div>

                    {/* Radius */}
                    <div>
                        <label style={{ fontSize: 12, display: "block", marginBottom: 4, color: "#555" }}>Radius</label>
                        <Input
                            type="number"
                            min={1}
                            className="generic-input-control"
                            value={radius}
                            onChange={(e) => setRadius(Math.max(1, Number(e.target.value)))}
                            style={{ marginBottom: 6 }}
                        />
                        <Select
                            className="custom-select"
                            value={radiusUnit}
                            onChange={setRadiusUnit}
                            style={{ width: "100%" }}
                            suffixIcon={<div className="dropdowncaret" />}
                        >
                            <Option value="mile">Mile</Option>
                            <Option value="km">KM</Option>
                        </Select>
                    </div>

                    <button
                        className="drawer-filled-btn"
                        onClick={handleSearch}
                        disabled={loading || geocodeStatus === "loading"}
                    >
                        {loading || geocodeStatus === "loading"
                            ? <Spin size="small" />
                            : <><FontAwesomeIcon icon={faSearch} />&nbsp; Search</>}
                    </button>

                    {/* Stats */}
                    {processed.length > 0 && (
                        <div style={{ borderTop: "1px solid #eee", paddingTop: 10, marginTop: "auto" }}>
                            <div style={{ fontSize: 12, color: "#22c55e", marginBottom: 4 }}>
                                <FontAwesomeIcon icon={faCircleCheck} />&nbsp; Suitable: <strong>{suitable.length}</strong>
                            </div>
                            <div style={{ fontSize: 12, color: "#ef4444" }}>
                                <FontAwesomeIcon icon={faCircleXmark} />&nbsp; Unsuitable: <strong>{unsuitable.length}</strong>
                            </div>
                        </div>
                    )}
                </div>

                {/* ── Main ── */}
                <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
                    <Tabs
                        activeKey={activeTab}
                        onChange={setActiveTab}
                        style={{ padding: "0 12px", flexShrink: 0 }}
                        items={[
                            { key: "table", label: `Available resources${processed.length ? ` (${processed.length})` : ""}`, children: null },
                            { key: "map",   label: "Map view of scheduled resources", children: null },
                        ]}
                    />

                    <div style={{ flex: 1, overflow: "hidden", padding: "0 12px 12px" }}>
                        {/* ── Table tab ── */}
                        {activeTab === "table" && (
                            loading ? (
                                <div style={{ display:"flex", justifyContent:"center", alignItems:"center", height:"100%" }}>
                                    <Spin size="large" tip="Searching employees…" />
                                </div>
                            ) : processed.length === 0 ? (
                                <div style={{ display:"flex", flexDirection:"column", justifyContent:"center", alignItems:"center", height:"100%", color:"#bbb", gap:8 }}>
                                    <FontAwesomeIcon icon={faSearch} style={{ fontSize:32 }} />
                                    <span>No employees found in your desired radius</span>
                                    <span style={{ fontSize:12 }}>Enter filters and click Search to find employees</span>
                                </div>
                            ) : (
                                <>
                                    {selectedRowKeys.length > 0 && (
                                        <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 8 }}>
                                            <button
                                                className="drawer-filled-btn"
                                                onClick={handlePublish}
                                                disabled={publishOpenDutiesLoading}
                                            >
                                                {publishOpenDutiesLoading ? <Spin size="small" /> : `Publish (${selectedRowKeys.length})`}
                                            </button>
                                        </div>
                                    )}
                                    <Table
                                        dataSource={processed}
                                        columns={columns}
                                        rowKey="_id"
                                        rowSelection={rowSelection}
                                        size="small"
                                        scroll={{ x: "max-content", y: "calc(76vh - 130px)" }}
                                        pagination={{ pageSize: 100, size: "small", 
                                            // showTotal: (t) => `${t} employees` 
                                        }}
                                        rowClassName={(r) =>
                                            r._isSuitable
                                                ? "sa-suitable-row"
                                                : "sa-unsuitable-row"
                                        }
                                    />
                                </>
                            )
                        )}

                        {/* ── Map tab ── */}
                        {activeTab === "map" && (
                            <div style={{ height: "100%", borderRadius: 6, overflow: "hidden", border: "1px solid #e8e8e8" }}>
                                <MapContainer
                                    center={mapCenter}
                                    zoom={refCoords ? 12 : 6}
                                    style={{ height: "100%", width: "100%" }}
                                >
                                    <TileLayer
                                        url="https://mt1.google.com/vt/lyrs=y&x={x}&y={y}&z={z}"
                                        attribution="Google Maps"
                                        maxZoom={20}
                                    />

                                    {/* Reference pin */}
                                    {refCoords && (
                                        <Marker position={[refCoords.lat, refCoords.lng]} icon={bluePin}>
                                            <Popup>
                                                <strong>{searchCoords ? `Postcode: ${postcode}` : site?.sitename}</strong>
                                            </Popup>
                                        </Marker>
                                    )}

                                    {/* Radius ring */}
                                    {refCoords && radius > 0 && (
                                        <Circle
                                            center={[refCoords.lat, refCoords.lng]}
                                            radius={radiusUnit === "km" ? radius * 1000 : radius * 1609.34}
                                            pathOptions={{ color: "#0091ae", weight: 1.5, fillOpacity: 0.05 }}
                                        />
                                    )}

                                    {/* Employee pins */}
                                    {processed.filter((e) => e._empCoords).map((emp) => (
                                        <Marker
                                            key={emp._id}
                                            position={[emp._empCoords.lat, emp._empCoords.lng]}
                                            icon={emp._isSuitable ? greenPin : redPin}
                                        >
                                            <Popup>
                                                <div style={{ minWidth: 170 }}>
                                                    <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:6 }}>
                                                        <Avatar
                                                            size={28}
                                                            src={emp?.metadata?.imageURL}
                                                            icon={!emp?.metadata?.imageURL && <UserOutlined />}
                                                            style={{ background:"#d9d9d9" }}
                                                        />
                                                        <div>
                                                            <div style={{ fontWeight:600, fontSize:12 }}>
                                                                {emp.firstname} {emp.lastname || ""}
                                                            </div>
                                                            <div style={{ color:"#888", fontSize:10 }}>{emp._phone}</div>
                                                        </div>
                                                    </div>

                                                    {emp._distance != null && (
                                                        <div style={{ fontSize:11, color:"#555", marginBottom:4 }}>
                                                            📍 {emp._distance.toFixed(1)} miles away
                                                        </div>
                                                    )}

                                                    {emp._pendingHoliday && (
                                                        <div style={{ fontSize:10, color:"#b7791f", lineHeight:1.4, marginBottom:4 }}>Leave Request Pending Approval</div>
                                                    )}
                                                    {emp._isSuitable
                                                        ? <Tag color="green" style={{ fontSize:10 }}>✓ Suitable</Tag>
                                                        : (
                                                            <>
                                                                <Tag color="red" style={{ fontSize:10 }}>✗ Unsuitable</Tag>
                                                                <div style={{ marginTop:4 }}>
                                                                    {emp._failLabels.map((l, i) => (
                                                                        <div key={i} style={{ fontSize:10, color:"#ef4444", lineHeight:1.4 }}>• {l}</div>
                                                                    ))}
                                                                </div>
                                                            </>
                                                        )
                                                    }

                                                    <button
                                                        className={emp._isSuitable ? "drawer-filled-btn" : "drawer-outlined-btn"}
                                                        style={{ fontSize:11, padding:"3px 10px", height:"auto", marginTop:8, width:"100%" }}
                                                        onClick={() => { onSelectEmployee?.(emp); close(); }}
                                                    >
                                                        Assign
                                                    </button>
                                                </div>
                                            </Popup>
                                        </Marker>
                                    ))}
                                </MapContainer>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <style>{`
                .sa-suitable-row   td { background: #f0fdf4 !important; }
                .sa-unsuitable-row td { background: #fff8f8 !important; }
                .sa-suitable-row:hover   td { background: #dcfce7 !important; }
                .sa-unsuitable-row:hover td { background: #fee2e2 !important; }
                .ant-table-thead > tr > th { background: #f5f5f5 !important; font-size: 12px !important; }
            `}</style>
        </Modal>
    );
};
